import { motion } from 'framer-motion';
import { useEffect, useMemo } from 'react';
import { AiOutlineFileDone } from 'react-icons/ai';
import { FaBriefcase } from 'react-icons/fa';
import { FaUsers } from 'react-icons/fa6';
import { IoDocumentAttach } from 'react-icons/io5';
import { LuFileCheck2 } from 'react-icons/lu';
import { RiContactsBook3Fill, RiFileForbidLine } from 'react-icons/ri';
import { Link } from 'react-router-dom';
import ApplicationContactInfo from '../../components/ApplicationComps/ApplicationContactInfo';
import ApplicationDocumentInfo from '../../components/ApplicationComps/ApplicationDocumentInfo';
import ApplicationProfessionalInfo from '../../components/ApplicationComps/ApplicationProfessionalInfo';
import ApplicationProfileInfo from '../../components/ApplicationComps/ApplicationProfileInfo';
import AcademiaNotFound from '../../components/CommonComps/AcademiaNotFound';
import AcademiaLoading from '../../components/CommonComps/Loadings/AcademiaLoading';
import PageTitle from '../../components/CommonComps/Titles/PageTitle';
import SectionHeader from '../../components/CommonComps/Titles/SectionHeader';
import { useApplicationContext } from '../../contexts/ApplicationProvider';
import { useAuthContext } from '../../contexts/AuthProvider';
import useApplicationStatusAction from '../../hooks/APIHooks/users/useApplicationStatusActions';
import { Associate } from '../../types/associates';

const ApplicationUpdate = () => {
  // integration of context hooks here
  const {
    setUsername,
    setSelectedLanguages,
    setSelectedServices,
    setSelectedCountry,
    setSelectedCountryCode,
    currentFormStage,
    setCurrentFormStage,
  } = useApplicationContext();

  const { currentUser } = useAuthContext();

  // integration of API hooks here
  const { getApplicationStatusHandler } = useApplicationStatusAction(
    currentUser ? (currentUser as Associate).uid : ''
  );

  const applicationDetails = useMemo(() => {
    if (!getApplicationStatusHandler.data?.is_success) return null;

    return getApplicationStatusHandler.data.data;
  }, [getApplicationStatusHandler.data]);

  console.log('APPLICATION DETAILS: ', applicationDetails);

  useEffect(() => {
    if (!applicationDetails) return;

    setUsername(applicationDetails.user.username);
    setSelectedCountryCode(applicationDetails.user_profile?.iso_code || '');
    setSelectedLanguages(
      applicationDetails.user_profile?.languages_spoken || []
    );
    setSelectedServices(
      applicationDetails.user_expertise.map((expertise) => ({
        id: expertise.service_id,
        name: expertise.service_name,
      }))
    );

    setSelectedCountry({
      id: 1000,
      name: applicationDetails.user_profile?.country || '',
      code: applicationDetails.user_profile?.iso_code,
    });
  }, [
    applicationDetails,
    setUsername,
    setSelectedCountryCode,
    setSelectedCountry,
    setSelectedLanguages,
    setSelectedServices,
  ]);

  useEffect(() => {
    if (typeof window !== 'undefined') {
      window.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
    }
  }, []);

  // rendering application update page component here
  return (
    <>
      {getApplicationStatusHandler.isLoading ? (
        <AcademiaLoading />
      ) : applicationDetails?.user.id ? (
        <motion.main
          initial={{ y: 100, opacity: 0 }}
          animate={{
            y: 0,
            opacity: 1,
            transition: {
              ease: 'easeInOut',
              y: { duration: 0.5 },
            },
          }}
          className='font-lato w-[95%] md:w-[90%] lg:w-4/5 2xl:w-3/5 max-w-[1500px] mx-auto py-5'
        >
          <PageTitle title='Update Application' />

          {applicationDetails.registration_request.status !== 'rejected' &&
          applicationDetails.registration_request.status !== 'approved' ? (
            <>
              <SectionHeader headerText='Update application' />

              <div className='bg-white shadow-academia-sm p-2 rounded-xl overflow-auto'>
                <div className='grid grid-cols-[repeat(4,_150px)] md:grid-cols-4 gap-2'>
                  <button
                    onClick={() => setCurrentFormStage('profile')}
                    className={`flex items-center justify-center gap-2 font-semibold rounded-lg hover:bg-base-light-blue/30 px-4 py-2 duration-300 ${
                      currentFormStage === 'profile'
                        ? 'bg-legal-dark-blue text-legal-brown hover:text-legal-dark-blue'
                        : 'text-legal-dark-blue bg-gray-100'
                    }`}
                  >
                    <FaUsers className='size-5' />
                    Profile
                  </button>

                  <button
                    title={
                      !applicationDetails?.user.first_name
                        ? 'Please, complete the profile section first!'
                        : undefined
                    }
                    onClick={() => setCurrentFormStage('contact')}
                    className={`flex items-center justify-center gap-2 font-semibold rounded-lg hover:bg-base-light-blue/30 px-4 py-2 duration-300 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-gray-100 ${
                      currentFormStage === 'contact'
                        ? 'bg-legal-dark-blue text-legal-brown hover:text-legal-dark-blue'
                        : 'text-legal-dark-blue bg-gray-100'
                    }`}
                    disabled={!applicationDetails?.user.first_name}
                  >
                    <RiContactsBook3Fill className='size-5' />
                    Contact
                  </button>

                  <button
                    title={
                      !applicationDetails?.user_profile?.address
                        ? 'Please, complete the contact section first!'
                        : undefined
                    }
                    onClick={() => setCurrentFormStage('professional')}
                    className={`flex items-center justify-center gap-2 font-semibold rounded-lg hover:bg-base-light-blue/30 px-4 py-2 duration-300 disabled:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-gray-100 ${
                      currentFormStage === 'professional'
                        ? 'bg-legal-dark-blue text-legal-brown hover:text-legal-dark-blue'
                        : 'text-legal-dark-blue bg-gray-100'
                    }`}
                    disabled={!applicationDetails?.user_profile?.address}
                  >
                    <FaBriefcase className='size-5' />
                    Professional
                  </button>

                  <button
                    title={
                      !applicationDetails?.user_profile?.business_reg_no
                        ? 'Please, complete the professional section first!'
                        : undefined
                    }
                    onClick={() => setCurrentFormStage('document')}
                    className={`flex items-center justify-center gap-2 font-semibold rounded-lg hover:bg-base-light-blue/30 px-4 py-2 duration-300 disabled:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-gray-100 ${
                      currentFormStage === 'document'
                        ? 'bg-legal-dark-blue text-legal-brown hover:text-legal-dark-blue'
                        : 'text-legal-dark-blue bg-gray-100'
                    }`}
                    disabled={
                      !applicationDetails?.user_profile?.business_reg_no
                    }
                  >
                    <IoDocumentAttach className='size-5' />
                    Document
                  </button>
                </div>
              </div>

              {applicationDetails?.registration_request.is_profile_complete ? (
                <div className='bg-white shadow-academia-sm px-4 py-3 rounded-xl my-3 space-y-2 border-2 border-base-light-blue'>
                  <h2 className='flex items-center gap-1 text-2xl font-semibold italic text-base-light-blue'>
                    <AiOutlineFileDone className='size-8' /> Application
                    Submitted
                  </h2>

                  <p className='text-legal-dark-gray'>
                    Your application has been submitted successfully. Our team
                    will review your profile and provide updates via email. You
                    may also use the{' '}
                    <Link
                      to='/application/tracking'
                      className='font-semibold text-base-cyan underline italic hover:opacity-70 duration-300'
                    >
                      Application Tracking
                    </Link>{' '}
                    tab at any time to monitor the progress of your application.
                  </p>
                </div>
              ) : null}

              <div className='space-y-5 mt-5'>
                {currentFormStage === 'profile' ? (
                  <ApplicationProfileInfo
                    applicationDetails={applicationDetails}
                  />
                ) : currentFormStage === 'contact' ? (
                  <ApplicationContactInfo
                    applicationDetails={applicationDetails}
                    defaultCountryCode={
                      applicationDetails?.user_profile?.iso_code
                    }
                  />
                ) : currentFormStage === 'professional' ? (
                  <ApplicationProfessionalInfo
                    applicationDetails={applicationDetails}
                  />
                ) : (
                  <ApplicationDocumentInfo />
                )}
              </div>
            </>
          ) : applicationDetails.registration_request.status === 'rejected' ? (
            <div className='bg-white shadow-academia-sm px-4 py-3 rounded-xl my-3 space-y-2 border-2 border-base-red'>
              <h2 className='flex items-center gap-1 text-2xl font-semibold italic text-base-red'>
                <RiFileForbidLine className='size-8' /> Application Rejected
              </h2>

              <p className='text-legal-dark-gray'>
                We regret to inform you that your application has not been
                successful. You may check the{' '}
                <Link
                  to='/application/tracking'
                  className='font-semibold text-base-cyan underline italic hover:opacity-70 duration-300'
                >
                  Application Tracking
                </Link>{' '}
                tab for further details, if available. We appreciate your
                interest in joining us and encourage you to review your profile
                or consider applying again in the future. Thank you for your
                time, and we wish you all the very best in your future
                endeavors.
              </p>
            </div>
          ) : (
            <div className='bg-white shadow-academia-sm px-4 py-3 rounded-xl my-3 space-y-2 border-2 border-base-cyan'>
              <h2 className='flex items-center gap-1 text-2xl font-semibold italic text-base-cyan'>
                <LuFileCheck2 className='size-8' /> Application Approved
              </h2>

              <p className='text-legal-dark-gray'>
                Congratulations! Your application has been approved. Our team is
                pleased to welcome you on board. Please check the{' '}
                <Link
                  to='/application/tracking'
                  className='font-semibold text-base-cyan underline italic hover:opacity-70 duration-300'
                >
                  Application Tracking
                </Link>{' '}
                tab for the next steps and any additional information. We
                appreciate your interest and look forward to your successful
                journey with us.
              </p>
            </div>
          )}
        </motion.main>
      ) : (
        <AcademiaNotFound message='Failed to load application details!' />
      )}
    </>
  );
};

export default ApplicationUpdate;
